import { OperationClientsData } from 'api/types/data/operation';
import { PrimaryClientType } from 'api/types/entity';
import {
  OperationExecutionMethod,
  OperationOrderEntity,
} from 'api/types/entity/operation';
import { setStartOfDay, yesOrNotSelectValidation } from 'helpers';
import {
  TypedForm,
  FormErrors as FormErrorsBase,
  ObjectSchema,
  object,
  selectSchema,
  string,
  date,
  number,
  array,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import { localFileScheme } from 'modules/client/common/helpers';
import { Maybe } from 'types/maybe';
import { SelectType } from 'types/selectType';
import { YesOrNoSelectType } from 'types/yesOrNo';

import { LocalFileEntity } from '../types';

export interface OperationFormType {
  clientType: Maybe<SelectType<PrimaryClientType>>;
  client: Maybe<{
    label: string;
    value: string;
    record?: OperationClientsData;
  }>;
  order: Maybe<{
    label: string;
    value: string;
    record?: OperationOrderEntity;
  }>;
  number: string;
  executedAt: Date;
  executionPlace: SelectType<string>;
  executionMethod?: Maybe<SelectType<OperationExecutionMethod>>;
  isSpecializedTrade: Maybe<YesOrNoSelectType>;
  trader: Maybe<SelectType<string>>;
  counterpartyName?: string;
  quantity: number;
  netUnitPrice: string;
  unitPrice?: string;
  accruedCouponIncome?: string;
  volume: string | undefined;
  settlementDate: Date;
  exchangeRate?: string;
  files?: LocalFileEntity[];
  additionalNotes?: string;
}

export const isDirectExecutionMethod = (
  value?: SelectType<OperationExecutionMethod> | null
) => value?.value === OperationExecutionMethod.Direct;

export const getSchema = (t: TFunc): ObjectSchema<OperationFormType> =>
  object({
    clientType: selectSchema(Object.values(PrimaryClientType))
      .default(null)
      .required(),
    client: object({
      value: string().required(),
      label: string().required(),
    })
      .default(null)
      .required(),
    order: object({
      value: string().required(),
      label: string().required(),
    })
      .default(null)
      .required(),
    number: string().max(200).required(),
    executedAt: date().max(new Date()).required(),
    executionPlace: object({
      value: string().required(),
      label: string().required(),
    })
      .default(null)
      .required(),
    executionMethod: selectSchema(Object.values(OperationExecutionMethod))
      .default(null)
      .required(),
    isSpecializedTrade: yesOrNotSelectValidation.default(null).required(),
    trader: object({
      value: string().required(),
      label: string().required(),
    })
      .default(null)
      .required(),
    counterpartyName: string().when('executionMethod', {
      is: isDirectExecutionMethod,
      then: (schema) => schema.required().max(200),
    }),
    quantity: number().min(1).required(),
    netUnitPrice: string().required(),
    unitPrice: string(),
    accruedCouponIncome: string(),
    volume: string().required(),
    settlementDate: date()
      .test(
        'settlementDate',
        t(
          'operation.sections.settlementSection.fields.settlementDate.validation.minDateError'
        ),
        (value, context) => {
          const { executedAt } = context.parent as OperationFormType;
          if (executedAt && value) {
            return (
              setStartOfDay(new Date(value)) >=
              setStartOfDay(new Date(executedAt))
            );
          }
          return true;
        }
      )
      .required(),
    exchangeRate: string(),
    files: array().of(localFileScheme.required()).max(10),
    additionalNotes: string().max(2000),
  });

export const { Field, Form, useFormContext, Submit, useField } =
  TypedForm<OperationFormType>();

export type FormErrors = FormErrorsBase<OperationFormType>;
